import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MeetList } from '@athleticnet/athletic-live-api-models';
import { combineLatest, firstValueFrom, lastValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SiteConfigInterface } from '../models/config/site-config.interface';
import { CurrentMeetService } from './current-meet.service';
import { SiteConfigService } from './site-config.service';
import { MeetDateService } from '../meet-date/meet-date.service';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(
    public currentMeetService: CurrentMeetService,
    public siteConfigService: SiteConfigService,
    public meetDateService: MeetDateService,
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: any,
  ) {}

  async setTitle(title: string | null) {
    await firstValueFrom(
      combineLatest([this.currentMeetService.updateMeet, this.siteConfigService.updateSiteConfig]).pipe(
        map(([currentMeet, siteConfig]: [MeetList | null, SiteConfigInterface | null]) => {
          let titleStr = title;
          if (currentMeet) {
            if (!titleStr) {
              titleStr =
                currentMeet.longName +
                ' - ' +
                this.meetDateService.transform(currentMeet.meetDates, currentMeet.timeFormat, currentMeet.dateString);
            } else {
              titleStr +=
                (titleStr !== currentMeet.longName ? ' - ' + currentMeet.longName : '') +
                ' - ' +
                this.meetDateService.transform(currentMeet.meetDates, currentMeet.timeFormat, currentMeet.dateString);
            }
          }
          if (siteConfig) {
            if (!titleStr) {
              titleStr = siteConfig.ngConfig.siteName;
            } else {
              titleStr += ' | ' + siteConfig.ngConfig.siteName;
            }
          }

          if (!titleStr) {
            titleStr = 'AthleticLIVE';
          }

          this.title.setTitle(titleStr);
          this.setTag('og:title', titleStr);
          this.setTag('twitter:title', titleStr);
        }),
      ),
    );
  }

  /**
   * Set the default tags for the site, should only be called in the root component
   */
  async setSiteDefaultTags() {
    // RESET ROBOT TAGS
    this.setRobotTags(false, false);

    await lastValueFrom(
      this.siteConfigService.updateSiteConfig.pipe(
        map((siteConfig: SiteConfigInterface | null) => {
          if (siteConfig) {
            // Set Images
            this.setTag(
              'og:image',
              environment.cdnEndpoint +
                '/assets/sites/' +
                siteConfig.ngConfig.machineName +
                (siteConfig.ngConfig.facebookImage ?? '/images/social/facebook-card-square.jpg'),
            );
            // this.setTag('og:image:type', 'image/jpeg'); // TODO: Change to dynamic
            this.setTag('og:image:alt', siteConfig.ngConfig.siteName + ' Facebook Share Logo');
            this.setTag(
              'twitter:image',
              environment.cdnEndpoint +
                '/assets/sites/' +
                siteConfig.ngConfig.machineName +
                (siteConfig.ngConfig.twitterImage ?? '/images/social/twitter-card-square.jpg'),
            );

            // Twitter specific
            if (siteConfig.ngConfig.twitterLink) {
              this.setTag('twitter:site', siteConfig.ngConfig.twitterLink);
            }

            // FB specific
            this.setTag(
              'fb:app_id',
              !!siteConfig.ngConfig.facebookAppId ? siteConfig.ngConfig.facebookAppId : '133201834024935',
            );

            // Set Description
            this.setDescription(siteConfig.ngConfig.aboutOneLiner);
          }
        }),
      ),
    );

    this.setTag('og:type', 'website');
    this.setTag('og:site_name', 'AthleticLIVE');

    // Supported Locales
    this.setTag('og:locale', 'en_US');
    this.setTag('og:locale:alternate', 'fr_FR');
  }

  setDescription(description: string) {
    this.setTag('description', description);
    this.setTag('og:description', description);
    this.setTag('twitter:description', description);
  }

  setCanonicalUrl(url: string) {
    const head = this.document.getElementsByTagName('head')[0];
    var element: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;
    if (element === null) {
      element = this.document.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', url);
    this.setTag('og:url', url);
    this.setHreflangs(head, url);
  }

  protected setHreflangs(head: any, url: string) {
    const defaultLocales = ['en', 'x-default'];

    defaultLocales.forEach((locale) => {
      this.setHreflangElement(head, url, locale);
    });
    const urlParts = url.split('/');
    environment.nonDefaultLocales.forEach((locale, idx) => {
      if (idx === 0) {
        urlParts.splice(3, 0, locale);
      } else {
        urlParts[3] = locale;
      }
      url = urlParts.join('/');
      this.setHreflangElement(head, url, locale);
    });
  }

  protected setHreflangElement(head: any, url: string, locale: string) {
    let element: HTMLLinkElement = this.document.querySelector(`link[hreflang='${locale}']`) || null;
    if (element === null) {
      element = this.document.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', locale);
    element.setAttribute('href', url);
  }

  setRobotTags(noIndex: boolean, noFollow: boolean) {
    let content = [];
    if (noIndex) {
      content.push('noindex');
    }
    if (noFollow) {
      content.push('nofollow');
    }

    if (content.length === 0) {
      this.removeTag('robots');
      this.removeTag('googlebot');
      return;
    }

    this.setTag('robots', content.join(', '));
    this.setTag('googlebot', content.join(', '));
  }

  /**
   * Indicate that the prerendered page is ready to be saved by the prerender service
   * @param timeout Time in milliseconds to wait before setting the prerenderReady flag
   */
  markAsLoaded(timeout = 500) {
    if (this.document.defaultView) {
      setTimeout(() => {
        const window = this.document.defaultView as any;
        window.prerenderReady = true;
      }, timeout);
    }
  }

  protected removeTag(tagName: string) {
    if (tagName.includes('og:') || tagName.includes('fb:')) {
      this.meta.removeTag("property='" + tagName + "'");
    } else {
      this.meta.removeTag("name='" + tagName + "'");
    }
  }

  protected setTag(tagName: string, content: string) {
    let toInsert;
    let existingTag = false;
    if (tagName.includes('og:') || tagName.includes('fb:')) {
      existingTag = !!this.meta.getTag("property='" + tagName + "'");
      toInsert = { property: tagName, content };
    } else {
      existingTag = !!this.meta.getTag("name='" + tagName + "'");
      toInsert = { name: tagName, content };
    }

    if (existingTag) {
      this.meta.updateTag(toInsert);
    } else {
      this.meta.addTag(toInsert);
    }
  }

  protected readonly environment = environment;
}
